import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  TextField,
  Button,
  Divider,
  Stack,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  InputAdornment,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteIcon from "@mui/icons-material/Delete";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ImageIcon from "@mui/icons-material/Image";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import EmojiPicker from "emoji-picker-react";

export default function WhatsAppAudiovisuales() {
  const navigate = useNavigate();
  const baseUrl = import.meta.env.BASE_URL || "/";
  const withBase = (path) => `${baseUrl}${path}`.replace(/\/{2,}/g, "/");
  // Estados para la plantilla de Meta WhatsApp API
  const [templateName, setTemplateName] = useState("tu_nombre_de_plantilla");
  const [templateNameError, setTemplateNameError] = useState("");
  const [templateLanguage, setTemplateLanguage] = useState("esPE");
  const [variableType, setVariableType] = useState("ninguno");
  const [multimediaContent, setMultimediaContent] = useState("Ninguna");
  const [templateBody, setTemplateBody] = useState("Hello");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [multimediaFile, setMultimediaFile] = useState(null);
  const [isLoadingMultimedia, setIsLoadingMultimedia] = useState(false);
  const [variableExamples, setVariableExamples] = useState({});
  const bodyTextareaRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Estados existentes
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState("");
  const [title, setTitle] = useState("");
  const [contentType, setContentType] = useState("audiovisual");
  const [days, setDays] = useState(3);
  const [hours, setHours] = useState("00:00");
  const [blockDays, setBlockDays] = useState("Lunes");
  const [blockFrom, setBlockFrom] = useState("09:00");
  const [blockTo, setBlockTo] = useState("12:00");
  const [specificDate, setSpecificDate] = useState("");
  const [specificHour, setSpecificHour] = useState("09:00");
  const [sendType, setSendType] = useState("lead");
  const [blockedTimes, setBlockedTimes] = useState([
    { day: "Lunes", from: "09:00", to: "12:00" },
  ]);
  const [newBlock, setNewBlock] = useState({
    day: "Lunes",
    from: "09:00",
    to: "12:00",
  });

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };
  const handleRemoveFile = () => setFile(null);

  const handleAddBlockedTime = () => {
    setBlockedTimes([...blockedTimes, { ...newBlock }]);
  };
  const handleRemoveBlockedTime = (idx) => {
    setBlockedTimes(blockedTimes.filter((_, i) => i !== idx));
  };
  const handleNewBlockChange = (field, value) => {
    setNewBlock({ ...newBlock, [field]: value });
  };

  // Funciones para la plantilla de Meta WhatsApp API
  const handleEmojiClick = (emojiData) => {
    const textarea = bodyTextareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = templateBody;
      const newText = text.substring(0, start) + emojiData.emoji + text.substring(end);
      setTemplateBody(newText);
      setShowEmojiPicker(false);
      // Restaurar el cursor después de actualizar
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + emojiData.emoji.length, start + emojiData.emoji.length);
      }, 0);
    }
  };

  const handleAddVariable = () => {
    const textarea = bodyTextareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = templateBody;
      const newText = text.substring(0, start) + "{{}}" + text.substring(end);
      setTemplateBody(newText);
      // Restaurar el cursor después de actualizar
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + 2, start + 2);
      }, 0);
    }
  };

  const handleBold = () => {
    const textarea = bodyTextareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = templateBody;
      const selectedText = text.substring(start, end);
      const newText = text.substring(0, start) + `*${selectedText}*` + text.substring(end);
      setTemplateBody(newText);
      setTimeout(() => {
        textarea.focus();
        if (selectedText) {
          textarea.setSelectionRange(start + 1, end + 1);
        } else {
          textarea.setSelectionRange(start + 1, start + 1);
        }
      }, 0);
    }
  };

  const handleItalic = () => {
    const textarea = bodyTextareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = templateBody;
      const selectedText = text.substring(start, end);
      const newText = text.substring(0, start) + `_${selectedText}_` + text.substring(end);
      setTemplateBody(newText);
      setTimeout(() => {
        textarea.focus();
        if (selectedText) {
          textarea.setSelectionRange(start + 1, end + 1);
        } else {
          textarea.setSelectionRange(start + 1, start + 1);
        }
      }, 0);
    }
  };


  // Cerrar emoji picker al hacer click fuera
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target) &&
        !event.target.closest('[data-emoji-button]')
      ) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmojiPicker]);

  // Validar tipo de archivo según el contenido multimedia seleccionado
  const validateFileType = (file, contentType) => {
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.split('.').pop();
    
    if (contentType === "Imagen") {
      const validExtensions = ['jpg', 'jpeg', 'png'];
      const validMimeTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      return validExtensions.includes(fileExtension) && validMimeTypes.includes(file.type);
    } else if (contentType === "Video") {
      const validExtensions = ['mp4'];
      const validMimeTypes = ['video/mp4'];
      return validExtensions.includes(fileExtension) && validMimeTypes.includes(file.type);
    } else if (contentType === "Documento") {
      const validExtensions = ['pdf'];
      const validMimeTypes = ['application/pdf'];
      return validExtensions.includes(fileExtension) && validMimeTypes.includes(file.type);
    }
    return false;
  };

  // Manejar cambio de archivo multimedia
  const handleMultimediaFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validar que el archivo coincida con el tipo seleccionado
      if (!validateFileType(selectedFile, multimediaContent)) {
        alert(`Tipo de archivo no válido. Por favor selecciona:\n${multimediaContent === "Imagen" ? "- JPG, JPEG o PNG" : multimediaContent === "Video" ? "- MP4" : "- PDF"}`);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }
      
      setIsLoadingMultimedia(true);
      setMultimediaFile(selectedFile);
      // Para todos los tipos, el loading se oculta después de un tiempo para dar la impresión de carga
      setTimeout(() => {
        setIsLoadingMultimedia(false);
      }, 500);
    }
  };


  // Manejar drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      // Validar que el archivo coincida con el tipo seleccionado
      if (!validateFileType(droppedFile, multimediaContent)) {
        alert(`Tipo de archivo no válido. Por favor selecciona:\n${multimediaContent === "Imagen" ? "- JPG, JPEG o PNG" : multimediaContent === "Video" ? "- MP4" : "- PDF"}`);
        return;
      }
      
      setIsLoadingMultimedia(true);
      setMultimediaFile(droppedFile);
      // Para todos los tipos, el loading se oculta después de un tiempo para dar la impresión de carga
      setTimeout(() => {
        setIsLoadingMultimedia(false);
      }, 500);
    }
  };

  const handleRemoveMultimediaFile = () => {
    setMultimediaFile(null);
    setIsLoadingMultimedia(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Box sx={{ background: "#f7f8fc", minHeight: "100vh", p: 4 }}>
      {/* Stepper */}
      <Box sx={{ mb: 4 }}>
        <Stepper
          activeStep={1}
          sx={{
            mb: 3,
            '& .MuiStepIcon-root.Mui-active': {
              color: '#6a8bff',
            },
            '& .MuiStepIcon-root.Mui-completed': {
              color: '#6a8bff',
            },
            '& .MuiStepLabel-label.Mui-active': {
              color: '#6a8bff',
              fontWeight: 'bold',
            },
            '& .MuiStepLabel-label.Mui-completed': {
              color: '#6a8bff',
              fontWeight: 'bold',
            },
          }}
        >
          <Step completed>
            <StepLabel>Datos de campaña</StepLabel>
          </Step>
          <Step active>
            <StepLabel>Selección de contenido</StepLabel>
          </Step>
          <Step>
            <StepLabel>Enviar campaña</StepLabel>
          </Step>
        </Stepper>
      </Box>
      {/* Título principal */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: "bold", color: "#031b4e", fontSize: 20 }}>
          WhatsApp - Audiovisuales
        </Typography>
      </Box>
      {/* Layout principal */}
      <Box sx={{ display: "flex", gap: 3 }}>
        {/* Columna izquierda */}
        <Box sx={{ flex: 2 }}>
          {/* Bloque 1: Muestra nombre e idioma */}
          <Card sx={{ p: 3, mb: 3, background: "#f8f9fa" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {/* Icono de bocina */}
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  background: "#128c7e",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"
                    fill="#fff"
                  />
                </svg>
              </Box>
              {/* Texto */}
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 17, color: "#222", mb: 1, fontWeight: 500 }}>
                  {templateName} • {templateLanguage === "esPE" ? "Spanish (PER)" : "English"}
                    </Typography>
                <Typography sx={{ fontSize: 14, color: "#222" }}>
                  Marketing
                </Typography>
              </Box>
            </Box>
          </Card>

          {/* Bloque 2: Nombre e idioma de la plantilla */}
          <Card sx={{ p: 4, mb: 3 }}>
            <Typography sx={{ fontWeight: "bold", mb: 3, fontSize: 18, color: "#031b4e" }}>
              Nombre e idioma de la plantilla
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 500, mb: 1, fontSize: 14 }}>
                  Asigna un nombre a la plantilla
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={templateName}
                  error={!!templateNameError}
                  helperText={templateNameError}
                  onChange={(e) => {
                    let value = e.target.value;
                    // Convertir a minúsculas
                    value = value.toLowerCase();
                    // Reemplazar espacios con guiones bajos
                    value = value.replace(/\s/g, '_');
                    // Filtrar solo caracteres permitidos: a-z, 0-9, _
                    value = value.replace(/[^a-z0-9_]/g, '');
                    // Validar longitud
                    if (value.length <= 512) {
                      setTemplateName(value);
                      // Limpiar error cuando el usuario empiece a escribir
                      if (templateNameError && value !== "tu_nombre_de_plantilla" && value.trim()) {
                        setTemplateNameError("");
                      }
                    }
                  }}
                  inputProps={{ maxLength: 512 }}
                      sx={{
                    background: "#fff",
                    "& .MuiOutlinedInput-root": {
                      height: "40px",
                      "& input": {
                        padding: "12px 12px",
                      }
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography sx={{ fontSize: 12, color: "#8a8fa7", mr: 1 }}>
                          {templateName.length}/512
                        </Typography>
                        {templateName.length === 0 ? (
                  <Box
                    sx={{
                              width: 16,
                              height: 16,
                              borderRadius: "50%",
                              border: "2px solid #f44336",
                              position: "relative",
                              ml: 0.5,
                      display: "flex",
                      alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Box
                              sx={{
                                position: "absolute",
                                width: "100%",
                                height: "2px",
                                background: "#f44336",
                                transform: "rotate(45deg)",
                    }}
                            />
                    </Box>
                        ) : templateName.length > 0 && templateName.length <= 512 ? (
                          <Box sx={{ color: "#4caf50", fontSize: 20 }}>✓</Box>
                        ) : null}
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 500, mb: 1, fontSize: 14 }}>
                  Selecciona un idioma
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={templateLanguage}
                    onChange={(e) => setTemplateLanguage(e.target.value)}
                  sx={{
                      background: "#fff",
                      "& .MuiOutlinedInput-root": {
                        height: "38px",
                        minHeight: "38px",
                        "& .MuiSelect-select": {
                          padding: "8px 12px",
                          height: "38px",
                          display: "flex",
                          alignItems: "center",
                        }
                      }
                  }}
                >
                    <MenuItem value="esPE">Spanish (PER)</MenuItem>
                    <MenuItem value="en">English</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Stack>
            </Card>

          {/* Bloque 3: Contenido */}
          <Card sx={{ p: 4, mb: 3 }}>
            <Typography sx={{ fontWeight: "bold", mb: 3, fontSize: 18, color: "#031b4e" }}>
              Contenido
            </Typography>
            <Stack spacing={3}>
              {/* Bloque de "Tipo de variable" - Comentado por ahora, se habilitará en el futuro (aproximadamente 1 año) */}
              {/* <Box>
                <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 1 }}>
                  Tipo de variable
                </Typography>
                <FormControl size="small" sx={{ width: "180px" }}>
                  <Select
                    value={variableType}
                    disabled
                    sx={{
                      background: "#fff",
                      "& .MuiOutlinedInput-root": {
                        height: "40px",
                        "& .MuiSelect-select": {
                          padding: "8px 12px",
                          height: "40px",
                        display: "flex",
                        alignItems: "center",
                        }
                      }
                    }}
                  >
                    <MenuItem value="ninguno">Ninguno</MenuItem>
                  </Select>
                </FormControl>
              </Box> */}
              <Box>
                <Typography sx={{ fontWeight: 500, mb: 1, fontSize: 14 }}>
                  Muestra de contenido multimedia • Opcional
                </Typography>
                <FormControl size="small" sx={{ width: "180px" }}>
                  <Select
                    value={multimediaContent}
                    onChange={(e) => {
                      const newContent = e.target.value;
                      setMultimediaContent(newContent);
                      // Limpiar el archivo cuando se cambia el tipo de contenido
                      if (newContent === "Ninguna" || (multimediaFile && !validateFileType(multimediaFile, newContent))) {
                        setMultimediaFile(null);
                        setIsLoadingMultimedia(false);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = "";
                        }
                      }
                    }}
                          sx={{
                      background: "#fff",
                      "& .MuiOutlinedInput-root": {
                        height: "36px",
                        "& .MuiSelect-select": {
                          padding: "8px 12px",
                        }
                      }
                    }}
                    renderValue={(selected) => {
                      return (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {selected === "Imagen" && <ImageIcon sx={{ fontSize: 20, color: "#8a8fa7" }} />}
                          {selected === "Video" && <VideoLibraryIcon sx={{ fontSize: 20, color: "#8a8fa7" }} />}
                          {selected === "Documento" && <InsertDriveFileIcon sx={{ fontSize: 20, color: "#8a8fa7" }} />}
                          <Typography>{selected}</Typography>
                    </Box>
                      );
                    }}
                  >
                    <MenuItem value="Ninguna">Ninguna</MenuItem>
                    <MenuItem value="Imagen">
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <ImageIcon sx={{ fontSize: 20, color: "#8a8fa7" }} />
                        <Typography>Imagen</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="Video">
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <VideoLibraryIcon sx={{ fontSize: 20, color: "#8a8fa7" }} />
                        <Typography>Video</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="Documento">
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <InsertDriveFileIcon sx={{ fontSize: 20, color: "#8a8fa7" }} />
                        <Typography>Documento</Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
                {/* Área de carga de archivo cuando se selecciona Imagen, Video o Documento */}
                {(multimediaContent === "Imagen" || multimediaContent === "Video" || multimediaContent === "Documento") && (
                  <Box
                      sx={{
                      mt: 2,
                      border: "1px dashed #bfc8e6",
                      borderRadius: 2,
                      p: 3,
                      background: "#f7f8fc",
                      cursor: "pointer",
                      }}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {!multimediaFile ? (
                      <Box sx={{ textAlign: "center" }}>
                        <CloudUploadIcon
                          sx={{ fontSize: 40, color: "#bfc8e6", mb: 1 }}
                        />
                        <Typography sx={{ color: "#8a8fa7", mb: 1, fontSize: 14 }}>
                          Arrastra y suelta un archivo para subirlo
                    </Typography>
                        <Typography
                          component="span"
                          sx={{
                            color: "#3451a1",
                            fontSize: 14,
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                        >
                          O elige archivos de tu dispositivo
                        </Typography>
                        <input
                          ref={fileInputRef}
                          type="file"
                          hidden
                          accept={
                            multimediaContent === "Imagen" 
                              ? ".jpg,.jpeg,.png,image/jpeg,image/png" 
                              : multimediaContent === "Video" 
                              ? ".mp4,video/mp4" 
                              : ".pdf,application/pdf"
                          }
                          onChange={handleMultimediaFileChange}
                        />
                      </Box>
                    ) : (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          justifyContent: "space-between",
                            background: "#fff",
                          borderRadius: 2,
                          p: 2,
                          border: "1px solid #e0e4f7",
                          }}
                        >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {multimediaContent === "Imagen" ? (
                            <ImageIcon sx={{ color: "#3451a1" }} />
                          ) : multimediaContent === "Video" ? (
                            <VideoLibraryIcon sx={{ color: "#3451a1" }} />
                          ) : (
                            <InsertDriveFileIcon sx={{ color: "#3451a1" }} />
                          )}
                          <Typography>{multimediaFile.name}</Typography>
                        </Box>
                        <IconButton onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveMultimediaFile();
                        }}>
                          <DeleteIcon color="error" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
              <Box sx={{ position: "relative" }}>
                <Typography sx={{ fontWeight: 500, mb: 1, fontSize: 14 }}>
                  Cuerpo
                </Typography>
                <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 1, background: "#fff", position: "relative" }}>
                  {/* Toolbar */}
                  <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                      gap: 1,
                      p: 1,
                      borderBottom: "1px solid #e0e0e0",
                      position: "relative",
                            }}
                          >
                    <Box sx={{ position: "relative" }}>
                      <IconButton
                            size="small"
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        data-emoji-button
                        sx={{ fontSize: 20 }}
                      >
                        😊
                      </IconButton>
                      {/* Emoji Picker */}
                      {showEmojiPicker && (
                        <Box
                          ref={emojiPickerRef}
                            sx={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            zIndex: 1000,
                            mt: 1,
                            boxShadow: 3,
                            borderRadius: 2,
                            overflow: "hidden",
                          }}
                        >
                          <EmojiPicker
                            onEmojiClick={handleEmojiClick}
                            height={350}
                            width={350}
                            searchDisabled={false}
                            previewConfig={{ showPreview: false }}
                          />
                        </Box>
                      )}
                      </Box>
                    <IconButton size="small" onClick={handleBold} sx={{ fontSize: 16, fontWeight: "bold" }}>
                      B
                    </IconButton>
                    <IconButton size="small" onClick={handleItalic} sx={{ fontSize: 16, fontStyle: "italic" }}>
                      I
                    </IconButton>
                    {/* Botón "Agregar variable" - Comentado por ahora, se habilitará en el futuro */}
                    {/* <Button
                      size="small"
                      onClick={handleAddVariable}
                      disabled={variableType === "ninguno"}
                          sx={{
                        textTransform: "none",
                        fontSize: 12,
                        color: "#3451a1",
                        "&:hover": { background: "#f5f8ff" },
                        "&:disabled": {
                          color: "#bdbdbd",
                          cursor: "not-allowed",
                        },
                          }}
                        >
                      + Agregar variable
                    </Button> */}
                    <Box sx={{ flex: 1 }} />
                  </Box>
                  {/* Textarea */}
                          <TextField
                    inputRef={bodyTextareaRef}
                    fullWidth
                    multiline
                    minRows={6}
                            size="small"
                    value={templateBody}
                    onChange={(e) => {
                      if (e.target.value.length <= 1024) {
                        setTemplateBody(e.target.value);
                      }
                    }}
                    placeholder="Escribe el contenido del mensaje aquí"
                    inputProps={{ maxLength: 1024 }}
                            sx={{
                      "& .MuiOutlinedInput-root": {
                              border: "none",
                        padding: "8px",
                        "& fieldset": {
                          border: "none",
                        },
                              },
                    }}
                  />
                  {/* Contador de caracteres */}
                  <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
                    <Typography sx={{ fontSize: 12, color: "#8a8fa7" }}>
                      {templateBody.length}/1024
                    </Typography>
                        </Box>
                      </Box>
                {/* Muestras de variables */}
                {(() => {
                  // Extraer todas las variables del formato {{variable}}
                  const variableRegex = /\{\{([^}]+)\}\}/g;
                  const variables = [];
                  let match;
                  while ((match = variableRegex.exec(templateBody)) !== null) {
                    const varName = match[1].trim();
                    if (varName && !variables.includes(varName)) {
                      variables.push(varName);
                    }
                  }
                  
                  if (variables.length > 0) {
                    return (
                      <Box sx={{ mt: 3 }}>
                        <Typography sx={{ fontWeight: "bold", mb: 1, fontSize: 16, color: "#031b4e" }}>
                          Muestras de variables
                      </Typography>
                        <Typography sx={{ fontSize: 14, color: "#8a8fa7", mb: 2 }}>
                          Incluye muestras de todas las variables de tu mensaje para que Meta pueda revisar la plantilla. Recuerda no incluir ningún dato del cliente para proteger su privacidad.
                      </Typography>
                        <Typography sx={{ fontWeight: 500, mb: 1, fontSize: 14 }}>
                          Cuerpo
                        </Typography>
                        <Stack spacing={2}>
                          {variables.map((varName) => (
                            <Box key={varName} sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                          <TextField
                                fullWidth
                            size="small"
                                value={`{{${varName}}}`}
                                InputProps={{
                                  readOnly: true,
                            }}
                                sx={{
                                  flex: "0 0 200px",
                                  background: "#f5f5f5",
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                      borderColor: "#e0e0e0",
                                    },
                                  },
                                }}
                          />
                          <TextField
                                fullWidth
                            size="small"
                                placeholder={`Ingresa contenido para {{${varName}}}`}
                                value={variableExamples[varName] || ""}
                            onChange={(e) => {
                                  setVariableExamples({
                                    ...variableExamples,
                                    [varName]: e.target.value,
                                  });
                                }}
                          sx={{
                                  background: "#fff",
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                      borderColor: "#e0e0e0",
                            },
                                  },
                                }}
                              />
                            </Box>
                          ))}
                      </Stack>
                    </Box>
                    );
                  }
                  return null;
                })()}
                  </Box>
            </Stack>
            {/* Botones de acción */}
            <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
                        <Button
                variant="outlined"
                onClick={() => {
                  navigate(withBase('dashboard/automatizacion/campaigns/new'), { state: { activeStep: 1 } });
                }}
                          sx={{
                  borderColor: "#6a8bff",
                  color: "#6a8bff",
                            textTransform: "none",
                  fontSize: 14,
                  fontWeight: 500,
                  padding: "8px 20px",
                  borderRadius: 2,
                            "&:hover": {
                    borderColor: "#5a7aef",
                    background: "#f5f8ff",
                    color: "#5a7aef",
                        },
                      }}
                    >
                Regresar
                        </Button>
              <Button
                variant="contained"
                onClick={() => {
                  // Validar que el nombre de la plantilla haya sido cambiado
                  if (templateName === "tu_nombre_de_plantilla" || !templateName.trim()) {
                    setTemplateNameError("Debes cambiar el nombre de la plantilla antes de continuar");
                    return;
                  }
                  setTemplateNameError("");
                  
                  // Extraer todas las variables del cuerpo del mensaje (formato {{variable}})
                  const variableRegex = /\{\{([^}]+)\}\}/g;
                  const variables = [];
                  let match;
                  // Resetear el regex para buscar desde el inicio
                  variableRegex.lastIndex = 0;
                  while ((match = variableRegex.exec(templateBody)) !== null) {
                    const varName = match[1].trim();
                    if (varName && !variables.includes(varName)) {
                      variables.push(varName);
                    }
                  }
                  
                  // Recopilar los parámetros de ejemplo de la sección "Muestras de variables"
                  // Convertir variableExamples al formato requerido: [{"name": "valor"}, {"fecha": "valor"}]
                  const parametrosEjemplo = variables
                    .filter(varName => variableExamples[varName] && variableExamples[varName].trim())
                    .map(varName => {
                      const obj = {};
                      obj[varName] = variableExamples[varName];
                      return obj;
                    });
                  
                  // Preparar datos de la plantilla para enviar
                  const plantillaData = {
                    nombre: templateName,
                    codigo_lenguaje: templateLanguage,
                    tipo_variable: variableType,
                    tipo_contenido: multimediaContent.toLowerCase(),
                    cuerpo: templateBody,
                    parametros_ejemplo: parametrosEjemplo.length > 0 ? parametrosEjemplo : []
                  };
                  
                  // Guardar datos de la plantilla en sessionStorage
                  sessionStorage.setItem('campaign_plantilla_data', JSON.stringify(plantillaData));
                  
                  // Guardar metadata del archivo multimedia si existe
                  if (multimediaFile) {
                    sessionStorage.setItem('campaign_multimedia_file_name', multimediaFile.name);
                    sessionStorage.setItem('campaign_multimedia_file_type', multimediaFile.type);
                    sessionStorage.setItem('campaign_multimedia_file_size', multimediaFile.size.toString());
                  }
                  
                  navigate(withBase('dashboard/automatizacion/campaigns/new/registrando'), {
                    state: {
                      plantilla: plantillaData,
                      multimediaFile: multimediaFile
                    }
                  });
                }}
                          sx={{
                  background: "#6a8bff",
                  color: "#fff",
                  textTransform: "none",
                  fontSize: 14,
                  fontWeight: 500,
                  padding: "8px 20px",
                  borderRadius: 2,
                  "&:hover": {
                    background: "#5a7aef",
                  },
                }}
              >
                Solicitar aprobación
              </Button>
            </Box>
          </Card>
        </Box>
        {/* Columna derecha: Vista previa */}
        <Box sx={{ flex: 1 }}>
          <Card sx={{ p: 3, minHeight: 400, background: "#ffffff", position: "sticky", top: 20 }}>
            <Typography sx={{ fontWeight: 600 }}>
              Vista previa
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                pt: 3,
                pb: 1,
              }}
            >
              {/* Frame del celular */}
              <Box
                sx={{
                  width: 290,
                  height: 600,
                  background: "#000",
                  borderRadius: "40px",
                  padding: "8px",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                  position: "relative",
                }}
              >
                {/* Notch superior */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 120,
                    height: 25,
                    background: "#000",
                    borderRadius: "0 0 20px 20px",
                    zIndex: 10,
                  }}
                />
                {/* Pantalla del celular */}
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                  background: "#fff",
                    borderRadius: "32px",
                    overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  }}
                >
                  {/* Espacio para el notch */}
                  <Box
                    sx={{
                      height: "25px",
                      background: "#fff",
                    }}
                  />
                  {/* Header de WhatsApp */}
                  <Box
                    sx={{
                      background: "#fff",
                      padding: "12px 4px",
                      display: "flex",
                  alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {/* Botón de regreso */}
                    <IconButton
                      sx={{
                        color: "#54656f",
                        padding: "4px",
                        minWidth: "32px",
                        "&:hover": { background: "rgba(0,0,0,0.05)" },
                }}
              >
                      <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                      </svg>
                    </IconButton>
                    {/* Avatar */}
                <Box
                  sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        background: "#6a8bff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                        color: "#fff",
                        fontWeight: "bold",
                        fontSize: 16,
                        flexShrink: 0,
                      }}
                    >
                      N
                    </Box>
                    {/* Información del contacto */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography
                          sx={{
                            color: "#111b21",
                            fontWeight: 600,
                            fontSize: 14,
                            lineHeight: 1.2,
                          }}
                        >
                    NOVALY
                  </Typography>
                  <Box
                    sx={{
                            width: 14,
                            height: 14,
                      borderRadius: "50%",
                            background: "#25d366",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            ml: 0.5,
                          }}
                        >
                          <svg width="8" height="8" fill="#fff" viewBox="0 0 24 24">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                          </svg>
                        </Box>
                      </Box>
                      <Typography
                        sx={{
                          color: "#667781",
                          fontSize: 12,
                          lineHeight: 1.2,
                        }}
                      >
                    Cuenta de empresa
                  </Typography>
                </Box>
                    {/* Menú de opciones */}
                    <IconButton
                      sx={{
                        color: "#54656f",
                        padding: "6px",
                        "&:hover": { background: "rgba(0,0,0,0.05)" },
                      }}
                    >
                      <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                      </svg>
                    </IconButton>
                  </Box>
                  {/* Área de chat con fondo con patrón */}
                <Box
                  sx={{
                    flex: 1,
                      background: "#efeae2",
                      backgroundImage: `
                        radial-gradient(circle at 20% 30%, rgba(0,0,0,0.02) 1px, transparent 1px),
                        radial-gradient(circle at 80% 70%, rgba(0,0,0,0.02) 1px, transparent 1px),
                        radial-gradient(circle at 40% 50%, rgba(0,0,0,0.015) 1px, transparent 1px),
                        radial-gradient(circle at 60% 20%, rgba(0,0,0,0.02) 1px, transparent 1px),
                        radial-gradient(circle at 10% 80%, rgba(0,0,0,0.015) 1px, transparent 1px),
                        repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(0,0,0,0.015) 35px, rgba(0,0,0,0.015) 36px),
                        repeating-linear-gradient(-45deg, transparent, transparent 35px, rgba(0,0,0,0.015) 35px, rgba(0,0,0,0.015) 36px)
                      `,
                      backgroundSize: "100% 100%, 100% 100%, 100% 100%, 100% 100%, 100% 100%, 50px 50px, 50px 50px",
                      padding: "16px 12px",
                      paddingBottom: 0,
                      overflowY: "auto",
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    {/* Mensaje del template */}
                    {templateName && (
                      <Box
                        sx={{
                          alignSelf: "flex-start",
                          maxWidth: "87%",
                          width: multimediaFile ? "100%" : "auto",
                          background: "#fff",
                          borderRadius: "8px 8px 8px 2px",
                          padding: "8px 12px",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                        }}
                      >
                        {isLoadingMultimedia ? (
                          <Box
                            sx={{
                    width: "100%",
                              aspectRatio: "16/9",
                              minHeight: 150,
                              maxHeight: 200,
                              borderRadius: "4px",
                              mb: 1,
                    display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: "#f5f5f5",
                              flexShrink: 0,
                  }}
                >
                            <Box sx={{ textAlign: "center" }}>
                              <CircularProgress size={40} sx={{ color: "#3451a1", mb: 1 }} />
                              <Typography sx={{ fontSize: 12, color: "#667781" }}>
                                Cargando...
                  </Typography>
                </Box>
                          </Box>
                        ) : (
                          <>
                            {multimediaContent === "Imagen" && multimediaFile && (
                <Box
                                component="img"
                                src={URL.createObjectURL(multimediaFile)}
                                alt="Preview"
                                onLoad={() => setIsLoadingMultimedia(false)}
                  sx={{
                    width: "100%",
                                  display: "block",
                                  borderRadius: "4px",
                                  mb: 1,
                                  maxHeight: 200,
                                  objectFit: "cover",
                                }}
                              />
                            )}
                            {multimediaContent === "Video" && multimediaFile && (
                              <Box
                                component="video"
                                src={URL.createObjectURL(multimediaFile)}
                                controls
                                onLoadedData={() => setIsLoadingMultimedia(false)}
                                sx={{
                                  width: "100%",
                                  display: "block",
                                  borderRadius: "4px",
                                  mb: 1,
                                  maxHeight: 200,
                                }}
                              />
                            )}
                            {multimediaContent === "Documento" && multimediaFile && (
                              <Box
                                sx={{
                                  width: "100%",
                                  borderRadius: "4px",
                                  padding: "16px",
                                  background: "#f5f5f5",
                    display: "flex",
                    alignItems: "center",
                                  gap: 2,
                                  mb: 1,
                  }}
                >
                                <InsertDriveFileIcon sx={{ fontSize: 40, color: "#3451a1" }} />
                                <Box>
                                  <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#111b21" }}>
                                    {multimediaFile.name}
                                  </Typography>
                                  <Typography sx={{ fontSize: 12, color: "#667781" }}>
                                    {(multimediaFile.size / 1024).toFixed(2)} KB
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </>
                        )}
                        <Typography
                          sx={{
                            fontSize: 14,
                            color: "#111b21",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                          dangerouslySetInnerHTML={{
                            __html: templateBody
                              .replace(/(^|\s)\*([^\s*][^*]*[^\s*])\*([\s.,;:!?¡¿]|$)/g, "$1<strong>$2</strong>$3")
                              .replace(/(^|\s)\*([^\s*]+)\*([\s.,;:!?¡¿]|$)/g, "$1<strong>$2</strong>$3")
                              .replace(/(^|\s)_([^\s_][^_]*[^\s_])_([\s.,;:!?¡¿]|$)/g, "$1<em>$2</em>$3")
                              .replace(/(^|\s)_([^\s_]+)_([\s.,;:!?¡¿]|$)/g, "$1<em>$2</em>$3")
                              .replace(/\{\{\}\}/g, '<span style="background: #fff3cd; padding: 2px 4px; border-radius: 3px;">{{}}</span>')
                              .replace(/\{\{([^}]+)\}\}/g, (match, varName) => {
                                const example = variableExamples[varName.trim()];
                                if (example) {
                                  return `<span style="background: #fff3cd; padding: 2px 4px; border-radius: 3px;">${example}</span>`;
                                }
                                return `<span style="background: #fff3cd; padding: 2px 4px; border-radius: 3px;">{{${varName}}}</span>`;
                              }),
                          }}
                        />
                      </Box>
                    )}
                    {!templateName && (
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: "rgba(0,0,0,0.4)",
                          textAlign: "center",
                          mt: 4,
                        }}
                      >
                        Completa el formulario para ver la vista previa
                      </Typography>
                    )}
                  </Box>
                  {/* Barra de mensajes */}
                  <Box
                    sx={{
                      background: "#efeae2",
                      backgroundImage: `
                        radial-gradient(circle at 20% 30%, rgba(0,0,0,0.02) 1px, transparent 1px),
                        radial-gradient(circle at 80% 70%, rgba(0,0,0,0.02) 1px, transparent 1px),
                        radial-gradient(circle at 40% 50%, rgba(0,0,0,0.015) 1px, transparent 1px),
                        radial-gradient(circle at 60% 20%, rgba(0,0,0,0.02) 1px, transparent 1px),
                        radial-gradient(circle at 10% 80%, rgba(0,0,0,0.015) 1px, transparent 1px),
                        repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(0,0,0,0.015) 35px, rgba(0,0,0,0.015) 36px),
                        repeating-linear-gradient(-45deg, transparent, transparent 35px, rgba(0,0,0,0.015) 35px, rgba(0,0,0,0.015) 36px)
                      `,
                      backgroundSize: "100% 100%, 100% 100%, 100% 100%, 100% 100%, 100% 100%, 50px 50px, 50px 50px",
                      padding: "8px 8px",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {/* Input de mensaje con iconos dentro */}
                  <TextField
                    placeholder="Mensaje"
                    size="small"
                    fullWidth
                      disabled
                      sx={{
                        background: "#fff",
                        borderRadius: "24px",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "24px",
                          paddingLeft: "2px",
                          paddingRight: "2px",
                          "& fieldset": {
                            border: "none",
                          },
                        },
                        "& .MuiInputBase-input": {
                          padding: "8px 8px",
                          fontSize: 15,
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ marginLeft: "12px" }}>
                            <IconButton
                              edge="start"
                              sx={{
                                color: "#54656f",
                                padding: "4px",
                                minWidth: "32px",
                                width: "32px",
                                height: "32px",
                                "&:hover": { background: "transparent" },
                              }}
                            >
                              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z" />
                              </svg>
                            </IconButton>
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end" sx={{ marginRight: "12px", display: "flex", gap: 0 }}>
                            {/* Icono de adjuntar */}
                            <IconButton
                              edge="end"
                              sx={{
                                color: "#54656f",
                                padding: "4px",
                                minWidth: "32px",
                                width: "32px",
                                height: "32px",
                                "&:hover": { background: "transparent" },
                              }}
                            >
                              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z" />
                              </svg>
                            </IconButton>
                            {/* Icono de cámara */}
                            <IconButton
                              edge="end"
                              sx={{
                                color: "#54656f",
                                padding: "4px",
                                minWidth: "32px",
                                width: "32px",
                                height: "32px",
                                "&:hover": { background: "transparent" },
                              }}
                            >
                              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 12m-3.2 0a3.2 3.2 0 1 0 6.4 0a3.2 3.2 0 1 0 -6.4 0" />
                                <path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" />
                              </svg>
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {/* Botón de micrófono verde (fuera del input) */}
                    <IconButton
                    sx={{
                        color: "#fff",
                      background: "#25d366",
                        padding: "10px",
                        minWidth: "40px",
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        "&:hover": { background: "#20ba5a" },
                        flexShrink: 0,
                    }}
                  >
                      <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
                    </svg>
                    </IconButton>
                  </Box>
                  {/* Barra de navegación inferior */}
                  <Box
                    sx={{
                      width: "100%",
                      height: "4px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      paddingTop: "16px",
                      paddingBottom: "8px",
                    }}
                  >
                    <Box
                      sx={{
                        width: "134px",
                        height: "4px",
                        background: "#000",
                        borderRadius: "2px",
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}
